import { createAjv } from "@jsonforms/core";
import { leaderboardEditDataMap } from "./common";

/**
 * Prepares the leaderboard payload from form data
 * @param {Object} createLeaderboardData - Form data
 * @returns {Object} - Formatted payload for API
 */
export const prepareLeaderboardPayload = (createLeaderboardData) => {
  return {
    title: createLeaderboardData.title,
    tieBreaker: createLeaderboardData?.tieBreaker?.[0]?.id,
    awardApproval: createLeaderboardData?.awardApproval?.[0]?.id,
    rankOrder: createLeaderboardData?.rankOrder?.[0]?.id,
    programId: createLeaderboardData.programId,
    activePointMinimum: createLeaderboardData.activePointMinimum,
    minParticipationCount: createLeaderboardData.minParticipationCount,
    endsAt: createLeaderboardData.endsAt,
    startsAt: createLeaderboardData.startsAt,
    scoringMode: createLeaderboardData?.scoringMode?.[0]?.id || "ACCRUAL",
  };
};

/**
 * Validates the leaderboard data against the schema
 * @param {Object} payload - Leaderboard data to validate
 * @param {Object} schema - JSON schema for validation
 * @returns {boolean} - Whether the data is valid
 */
export const validateLeaderboardData = (payload, schema) => {
  const ajv = createAjv();
  const isDataValid = ajv.validate(schema, payload);
  return isDataValid;
};

/**
 * Validates start and end dates for a leaderboard
 * @param {Object} dateData - Object containing startsAt and endsAt
 * @returns {Object} - Validation result with status and message
 */
export const validateStartEndDates = (dateData) => {
  const { startsAt, endsAt } = dateData;

  if (!startsAt || !endsAt) {
    return { isValid: false, message: "Both start and end dates must be provided." };
  }

  const startDate = new Date(startsAt);
  const endDate = new Date(endsAt);

  if (startDate > endDate) {
    return {
      isValid: false,
      message: "Start date cannot be later than the end date.",
    };
  }

  return { isValid: true };
};

/**
 * Checks if a leaderboard with the same title already exists
 * @param {Array} leaderboardsList - List of existing leaderboards
 * @param {string} title - Title to check
 * @param {string} currentId - ID of the current leaderboard (for edit mode)
 * @returns {boolean} - Whether a leaderboard with the same title exists
 */
export const checkLeaderboardExists = (leaderboardsList, title, currentId) => {
  return leaderboardsList?.some(
    (item) =>
      item.title.toUpperCase() === title.trim().toUpperCase() &&
      item.id !== currentId
  );
};

/**
 * Handles form data initialization based on selected leaderboard
 * @param {Object} selectedLeaderboard - Selected leaderboard for editing
 * @param {Object} initialState - Initial state for the form
 * @returns {Object} - Initialized form data
 */
export const initializeFormData = (selectedLeaderboard, initialState) => {
  if (selectedLeaderboard?.id) {
    // Edit mode - populate with selected leaderboard data
    const mappedData = leaderboardEditDataMap(selectedLeaderboard);
    return { ...mappedData };
  }
  // Create mode - reset to initial state
  return initialState;
};

/**
 * Updates field properties based on leaderboard state
 * @param {Object} selectedLeaderboard - Selected leaderboard
 * @param {Function} addKeyValueToElement - Function to add key-value to element
 */
export const updateFieldProperties = (selectedLeaderboard, addKeyValueToElement) => {
  const startsAt = selectedLeaderboard?.startsAt;
  const isActive = startsAt ? new Date(startsAt) < new Date() : false;

  if (selectedLeaderboard?.id && isActive) {
    addKeyValueToElement("#/properties/activePointMinimum", "disabled", true);
    addKeyValueToElement("#/properties/minParticipationCount", "disabled", true);
    addKeyValueToElement("#/properties/tieBreaker", "disabled", true);
    addKeyValueToElement("#/properties/rankOrder", "disabled", true);
    if (isActive) {
      addKeyValueToElement("#/properties/startsAt", "disabled", true);
    } else {
      addKeyValueToElement("#/properties/startsAt", "disabled", false);
    }
  } else {
    addKeyValueToElement("#/properties/activePointMinimum", "disabled", false);
    addKeyValueToElement("#/properties/minParticipationCount", "disabled", false);
    addKeyValueToElement("#/properties/tieBreaker", "disabled", false);
    addKeyValueToElement("#/properties/rankOrder", "disabled", false);
    addKeyValueToElement("#/properties/startsAt", "disabled", false);
  }
};
